import supabase from '../supabaseClient';
import { logOnboardingError } from './errorLogger';

/**
 * Deletes profile images from Supabase storage.
 * @param {string} userId - The auth user ID
 * @returns {Promise<void>}
 */
async function deleteProfileImages(userId) {
  try {
    // Get the user's profile to find any profile images
    const { data: profile } = await supabase
      .from('profiles')
      .select('profile_image_url')
      .eq('auth_user_id', userId)
      .maybeSingle();

    // Delete profile image from storage if it exists
    if (profile?.profile_image_url) {
      try {
        // Extract the file path from the URL
        const url = new URL(profile.profile_image_url);
        const pathParts = url.pathname.split('/');
        const bucketName = 'profile-images';
        const fileName = pathParts[pathParts.length - 1];

        if (fileName) {
          const { error: storageError } = await supabase.storage
            .from(bucketName)
            .remove([fileName]);

          if (storageError) {
            console.warn('Failed to delete profile image:', storageError);
          }
        }
      } catch (err) {
        console.warn('Error parsing profile image URL:', err);
      }
    }
  } catch (err) {
    console.warn('Error deleting profile images:', err);
  }
}

/**
 * Initiates account deletion for the current user.
 * This function implements Apple's account deletion requirements.
 *
 * Data deletion includes:
 * - Profile data
 * - Fitness test results
 * - Profile images from storage
 * - Scheduled workouts
 * - Error logs
 * - Rivalry data (all associated rivalries, participants, messages, etc.)
 * - Fighter profiles
 * - All other user-associated data
 *
 * @returns {Promise<{success: boolean, error: Error|null, message?: string}>}
 */
export async function deleteCurrentUserAccount() {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error('No authenticated user found');
    }

    // Step 1: Delete profile images from storage
    await deleteProfileImages(user.id);

    // Step 2: Call the database function to delete all user data
    const { data, error: rpcError } = await supabase.rpc('delete_user_account');

    if (rpcError) {
      console.error('RPC error during account deletion:', rpcError);
      throw new Error(`Failed to delete account data: ${rpcError.message}`);
    }

    // Check the result from the function
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to delete account data');
    }

    // Step 3: Sign out the user
    const { error: signOutError } = await supabase.auth.signOut();

    if (signOutError) {
      console.warn('Error signing out after deletion:', signOutError);
    }

    return {
      success: true,
      error: null,
      message: data.message || 'Account deleted successfully'
    };

  } catch (error) {
    console.error('Error deleting account:', error);

    // Log the deletion failure
    try {
      await logOnboardingError({
        error,
        action: 'delete_account',
      });
    } catch (logError) {
      console.warn('Failed to log deletion error:', logError);
    }

    return {
      success: false,
      error: error instanceof Error ? error : new Error('Failed to delete account')
    };
  }
}
